import React, { useCallback, useState, useRef } from 'react';
import { Tooltip, message, Switch, Button, Icon } from 'antd';
import { useTranslation } from 'react-i18next';
import DocumentName from '../../components/DocumentName';
import EmailAutoSaveButton from './EmailAutoSaveButton';
import {
  useDocumentContext,
  useComplexDocumentHistory,
  COMPLEX_DOCUMENT_CONTEXT_KEYS,
} from '../../context/complexDocumentContext';
import { useArtboardContext, CONTEXT_KEYS } from '../../context/complexArtboardContext';
import { useEmailContext, COMPLEX_EMAIL_CONTEXT_KEYS } from '../../context/complexEmailContext';
import { ActionButton, UndoIcon, RedoIcon, EndContainer, PreviewEndContainer } from '../ComplexDocumentDesign/styles';
import { TemplateName } from '../SidebarMenu/styles';
import { COMPLEX_DESIGN_TOOLBAR } from '../ComplexDocumentDesign/translationValues';
import { COMPLEX_EMAIL_TOOLBAR } from './translationValues';
import {
  lockResource,
  unlockResource,
  checkLockStatus,
  updateCommunicationName
} from '../../utils/services/complexDocumentsAPI';
import { COMPLEX_DOCUMENT_PAGE_WRAPPER } from '../../utils/complexDocumentConstants';
import ComplexEmailVersion from './ComplexEmailVersion';
import { useSessionContext } from '../../context/sessionContext';
import { ELEMENT_POSITION_TOOLBAR_ID } from '../../artboard/Toolbar/elementPositionToolbar';
import { updateEmailResourceNames } from '../../utils/services/complexEmailAPI';
import { TEMPLATE_NAME_CHAR_LIMIT } from '../../utils/constants/appConstants';
import { checkUserAccessRoles, extractUnsupportedCharacters } from '../../utils/helper';
import { useVersionContext, VERSION_CONTEXT_KEYS } from '../../context/complexVersionContext';
import { ApprovalProcess } from '../ComplexDocumentDesign/ApprovalProcess/ApprovalProcess';
import { truncateText } from '../../utils/helper';

const NAMESPACES = [
  'TEMPLATES_DESIGN_AREA_TOOLBAR',
  'COMPLEX_DESIGN_TOOLBAR',
  'COMPLEX_DOCUMENT_ENVIRONMENT',
  'COMPLEX_EMAIL_TOOLBAR',
  'COMPLEX_EMAIL_PREVIEW_SETTINGS'
];

const EmailTemplateName = ({ editable }) => {
  const { t } = useTranslation(NAMESPACES);
  const containerRef = useRef(null);
  const pageWrapperTop = useRef(null);
  const customSelector = useCallback(
    store => ({
      resourceName: store[COMPLEX_DOCUMENT_CONTEXT_KEYS.DOCUMENT_NAME],
      resourceId: store[COMPLEX_DOCUMENT_CONTEXT_KEYS.ID],
      communicationId: store[COMPLEX_DOCUMENT_CONTEXT_KEYS.COMMUNICATION_ID]
    }),
    [],
  );
  const [docInfo, store] = useDocumentContext(customSelector, `EmailTemplateName`);
  const [blur, setBlur] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [{ undoStack, redoStack }] = useComplexDocumentHistory(store => ({
    undoStack: store.undoStack,
    redoStack: store.redoStack,
  }));

  const templateLockedSelector = useCallback(store => store[CONTEXT_KEYS.TEMPLATE_LOCKED], []);
  const [isTemplateLocked] = useArtboardContext(templateLockedSelector, `EmailTemplateName`);

  const lockedUserIdSelector = useCallback(store => store[CONTEXT_KEYS.LOCKED_USER_ID], []);
  const [lockedUserID] = useArtboardContext(lockedUserIdSelector, `EmailTemplateName`);

  const currentBlockSelector = useCallback(store => store[CONTEXT_KEYS.CURRENT_BLOCK], []);
  const [currentBlock] = useArtboardContext(currentBlockSelector, `EmailTemplateName`);

  const currentDesignSelector = useCallback(store => store[CONTEXT_KEYS.CURRENT_DESIGN], []);
  const [currentDesign, updateArtboard] = useArtboardContext(
    currentDesignSelector,
    `EmailTemplateName`,
  );

  const sessionSelector = useCallback(store => store.userData, []);
  const [userDetails] = useSessionContext(sessionSelector);

  const previewSelector = useCallback(store => store[CONTEXT_KEYS.IS_PREVIEW], []);
  const [isPreview] = useArtboardContext(previewSelector, `EmailTemplateName`);

  const sendingEmailSelector = useCallback(store => store[CONTEXT_KEYS.GENERATING_PDF], []);
  const [sendingEmail] = useArtboardContext(sendingEmailSelector, `ComplexDocumentName`);

  const previewDownloadSelector = useCallback(store => store[CONTEXT_KEYS.PREVIEW_AND_DOWNLOAD], []);
  const [isPreviewDownload] = useArtboardContext(previewDownloadSelector, `EmailTemplateName`);

  const mobileViewSelector = useCallback(store => store[COMPLEX_EMAIL_CONTEXT_KEYS.IS_MOBILE_VIEW], []);
  const [, emailMethods] = useEmailContext(mobileViewSelector);

  const versionSettingsVisibleSelector = useCallback(
    store => store[VERSION_CONTEXT_KEYS.VERSION_SETTINGS_VISIBLE],
    [],
  );
  const [versionSettingsVisible] = useVersionContext(versionSettingsVisibleSelector, 'EmailTemplateName');

  const visibleTagSelector = useCallback(
    store => store[VERSION_CONTEXT_KEYS.CURRENT_VISIBLE_TAG],
    [],
  );
  const [currentTag] = useVersionContext(visibleTagSelector, `EmailTemplateName`);

  const currentVersionSelector = useCallback(store => store[VERSION_CONTEXT_KEYS.CURRENT_VISIBLE_VERSION], []);
  const [currentVersion] = useVersionContext(currentVersionSelector, `EmailTemplateName`);

  const artboardVersionSelector = useCallback(store => store[CONTEXT_KEYS.ARTBOARD_VERSION], []);
  const [artboardVersion] = useArtboardContext(artboardVersionSelector, 'EmailTemplateName');

  const readonlySelector = useCallback(store => store[CONTEXT_KEYS.READONLY_TEMPLATE], []);
  const [readOnly] = useArtboardContext(readonlySelector, 'EmailTemplateName');

  const undoNameChange = useCallback(() => {
    setBlur(false);
    const blurTimeOut = setTimeout(() => {
      setBlur(true);
      clearTimeout(blurTimeOut);
    }, 100);
  }, []);

  const changeTemplateName = async input => {
    // Validations: The template name must not be empty, and it should differ from the previous one
    // If the input value is empty, restore the previous template name
    const name = input.trim();
    if(name.length > TEMPLATE_NAME_CHAR_LIMIT) {
      message.error(t(COMPLEX_DESIGN_TOOLBAR.MAX_CHAR_LIMIT_EXCEEDED_ERROR.key, COMPLEX_DESIGN_TOOLBAR.MAX_CHAR_LIMIT_EXCEEDED_ERROR.value, { charLimitLength: TEMPLATE_NAME_CHAR_LIMIT }));
      return;
    }
    if (name.length < 1) {
      undoNameChange();
      message.warn(
        t(
          COMPLEX_DESIGN_TOOLBAR.TEMPLATE_NAME_EMPTY_WARNING_MESSAGE.key,
          COMPLEX_DESIGN_TOOLBAR.TEMPLATE_NAME_EMPTY_WARNING_MESSAGE.value,
        ),
      );
      return;
    }

    if (name !== docInfo.resourceName) {
      let payload = { name };
      if (currentDesign === 'document') {
        payload.templateId = docInfo.resourceId;
      } else if (currentDesign === 'block') {
        payload.blockId = docInfo.resourceId;
      }
      let response;
      if (currentDesign === 'document') {
        response = await updateCommunicationName(docInfo.communicationId, name);
      } else {
        response = await updateEmailResourceNames(payload, currentDesign !== 'document');
      }
      if (response?.statusCode === 1804 || response?.statusCode === 1927 || response.statusCode === 3201) {
        store.set({ [COMPLEX_DOCUMENT_CONTEXT_KEYS.DOCUMENT_NAME]: name });
      } else {
        if(response?.statusCode === 1941 || response?.statusCode === 2616) {
          undoNameChange();
        } else {
          if(response?.statusCode === 3200 && response?.errors?.some((error => error?.code === 3003))) {
            message.error(t(
              COMPLEX_DESIGN_TOOLBAR.COMMUNICATION_NAME_EXIST_ERROR.key,
              COMPLEX_DESIGN_TOOLBAR.COMMUNICATION_NAME_EXIST_ERROR.value
            ));
          } else if (response?.statusCode === 3200 && response?.errors?.some((error => error?.code === 5601))) {
            const error = response?.errors?.[0];
            const unsupportedChars = extractUnsupportedCharacters(error.description);

            message.error(
              t(
                COMPLEX_DESIGN_TOOLBAR.UPDATE_TEMPLATE_NAME_UNSUPPORTED_CHARS.key,
                COMPLEX_DESIGN_TOOLBAR.UPDATE_TEMPLATE_NAME_UNSUPPORTED_CHARS.value,
                { char: unsupportedChars }
              )
            );
            undoNameChange();
          } else {
          message.error(
            t(
              COMPLEX_DESIGN_TOOLBAR.UPDATE_TEMPLATE_NAME_ERROR_MESSAGE.key,
              COMPLEX_DESIGN_TOOLBAR.UPDATE_TEMPLATE_NAME_ERROR_MESSAGE.value,
            ));
          }
        }
      }
    }
  };

  const handleReadModeChange = async e => {
    let payload = {
      resourceId: docInfo.resourceId,
      resourceType: currentDesign === 'document' ? 'email' : currentDesign === 'block' ? 'email_block' : undefined
    };
    setIsLoading(true);
    try {
      const lockStatus = await checkLockStatus(payload);
      if (lockStatus.statusCode === 741) {
        if (userDetails.id !== lockStatus.data.userId) {
          updateArtboard({
            [CONTEXT_KEYS.TEMPLATE_LOCKED]: true,
            [CONTEXT_KEYS.LOCKED_USER_ID]: lockStatus.data.userId,
          });
          return;
        }
      }
      if (!isTemplateLocked) {
        const response = await unlockResource(payload);
        if (response.statusCode === 721) {
          updateArtboard({
            [CONTEXT_KEYS.PAGE_LAYOUT_VISIBLE]: false,
            [CONTEXT_KEYS.CURRENT_BLOCK]: '',
            [CONTEXT_KEYS.SWITCH_CREATIVE_DESIGN_MENU]: false,
            [CONTEXT_KEYS.TEMPLATE_LOCKED]: true,
            [CONTEXT_KEYS.ENABLE_EDITABLE_DESIGN]: false,
            [CONTEXT_KEYS.IS_DRAFT_EDITABLE]: false,
          });
        } else {
          message.error(
            t(
              COMPLEX_DESIGN_TOOLBAR.ERROR_SWITCHING_READ_MODE.key,
              COMPLEX_DESIGN_TOOLBAR.ERROR_SWITCHING_READ_MODE.value,
            ),
          );
        }
      } else {
        const response = await lockResource(payload);
        if (response.statusCode === 719) {
          updateArtboard({
            [CONTEXT_KEYS.TEMPLATE_LOCKED]: false,
            [CONTEXT_KEYS.ENABLE_EDITABLE_DESIGN]: true,
            [CONTEXT_KEYS.IS_DRAFT_EDITABLE]: true,
          });
        } else {
          message.error(
            t(
              COMPLEX_DESIGN_TOOLBAR.ERROR_SWITCHING_EDITABLE_MODE.key,
              COMPLEX_DESIGN_TOOLBAR.ERROR_SWITCHING_EDITABLE_MODE.value,
            ),
          );
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePreviewDownload = () => {
    updateArtboard({ [CONTEXT_KEYS.PREVIEW_AND_DOWNLOAD]: !isPreviewDownload });
    const complexPageWrapper = document.getElementById(COMPLEX_DOCUMENT_PAGE_WRAPPER);
    if (!isPreviewDownload === false) {
      if (pageWrapperTop.current !== null) {
        complexPageWrapper.scrollTop = pageWrapperTop.current;
        pageWrapperTop.current = null;
      }
      emailMethods.set({
        [COMPLEX_EMAIL_CONTEXT_KEYS.IS_MOBILE_VIEW]: false
      });
    } else {
      pageWrapperTop.current = complexPageWrapper.scrollTop;
    }
  };

  const versionMap = {
      draft: t(COMPLEX_DESIGN_TOOLBAR.VERSION_DRAFT.key, COMPLEX_DESIGN_TOOLBAR.VERSION_DRAFT.value),
      qaready: t(
        COMPLEX_DESIGN_TOOLBAR.VERSION_TESTING.key,
        COMPLEX_DESIGN_TOOLBAR.VERSION_TESTING.value,
      ),
      sandbox: t(
        COMPLEX_DESIGN_TOOLBAR.VERSION_TESTING.key,
        COMPLEX_DESIGN_TOOLBAR.VERSION_TESTING.value,
      ),
      published: t(
        COMPLEX_DESIGN_TOOLBAR.VERSION_PUBLISHED.key,
        COMPLEX_DESIGN_TOOLBAR.VERSION_PUBLISHED.value,
      ),
      live: t(
        COMPLEX_DESIGN_TOOLBAR.VERSION_PUBLISHED.key,
        COMPLEX_DESIGN_TOOLBAR.VERSION_PUBLISHED.value,
      ),
    };
  
    const versionName = versionMap[currentVersion];

  return (
    <>
      <TemplateName ref={containerRef} previewEnabled={isPreview}>
        {isPreview && <div class="blur-overlay"></div>}
        <div>
          <DocumentName
            disabled={!editable}
            templateName={docInfo.resourceName}
            changeTemplateName={changeTemplateName}
            onBlur={blur}
            isElement={false}
          />
          {versionSettingsVisible && <Icon type="branches" />}
          {versionSettingsVisible && <p className='current-version'>
            {currentTag?.tagName ? <>{truncateText(currentTag.tagName, 20)} - {versionMap[currentTag.env]}</> : versionName}
          </p>}
          {editable && (
            <>
              <Tooltip
                placement="bottom"
                title={t(
                  `COMPLEX_DESIGN_TOOLBAR:${COMPLEX_DESIGN_TOOLBAR.UNDO_TOOLTIP.key}`,
                  COMPLEX_DESIGN_TOOLBAR.UNDO_TOOLTIP.value,
                )}
              >
                <ActionButton
                  id="undoBtn"
                  disabled={!editable && undoStack.length === 0}
                  onClick={store.undo}
                >
                  <UndoIcon color={undoStack.length === 0 ? '#6a7580' : '#fff'} />
                </ActionButton>
              </Tooltip>
              <Tooltip
                placement="bottom"
                title={t(
                  `COMPLEX_DESIGN_TOOLBAR:${COMPLEX_DESIGN_TOOLBAR.REDO_TOOLTIP.key}`,
                  COMPLEX_DESIGN_TOOLBAR.REDO_TOOLTIP.value,
                )}
              >
                <ActionButton
                  id="redoBtn"
                  disabled={!editable && redoStack.length === 0}
                  onClick={store.redo}
                >
                  <RedoIcon color={redoStack.length === 0 ? '#6a7580' : '#fff'} />
                </ActionButton>
              </Tooltip>
              <Tooltip
                placement="bottom"
                title={t(
                  `COMPLEX_DESIGN_TOOLBAR:${COMPLEX_DESIGN_TOOLBAR.AUTO_SAVE_CHANGES_TOOLTIP.key}`,
                  COMPLEX_DESIGN_TOOLBAR.AUTO_SAVE_CHANGES_TOOLTIP.value,
                )}
              >
                <EmailAutoSaveButton editable={editable} noInternetTooltip={t(
                  `COMPLEX_DESIGN_TOOLBAR:${COMPLEX_DESIGN_TOOLBAR.NO_INTERNET_TOOLTIP.key}`,
                  COMPLEX_DESIGN_TOOLBAR.NO_INTERNET_TOOLTIP.value,
                 )} />
              </Tooltip>
            </>
          )}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'start',
            width: '100%',
          }}
        >
          <div
            id="document-editor-toolbar"
            style={{
              margin: 5,
              height: 30,
              position: 'relative',
              color: 'black',
              display: 'flex',
              alignItems: 'center',
            }}
          />
          <div id={ELEMENT_POSITION_TOOLBAR_ID} />
        </div>
        <PreviewEndContainer isPreview={isPreview}>
          <ApprovalProcess isEmail={true} />
          {!currentBlock && (
            <>
            <div id="email-preview-options" />
            <Button ghost onClick={handlePreviewDownload} loading={sendingEmail}  className={isPreview && 'exit_preview'}>
              {isPreview
                ? checkUserAccessRoles(userDetails, ['DESIGNER', 'PUBLISHER']) && currentDesign === 'document' ? t(
                    `COMPLEX_EMAIL_TOOLBAR:${COMPLEX_EMAIL_TOOLBAR.EXIT_PREVIEW_AND_SEND_BUTTON.key}`,
                    COMPLEX_EMAIL_TOOLBAR.EXIT_PREVIEW_AND_SEND_BUTTON.value,
                  ) : t(
                    `COMPLEX_EMAIL_TOOLBAR:${COMPLEX_EMAIL_TOOLBAR.EXIT_PREVIEW_BUTTON.key}`,
                    COMPLEX_EMAIL_TOOLBAR.EXIT_PREVIEW_BUTTON.value,
                  )
                : sendingEmail
                  ? 'Sending Emails...'
                  : checkUserAccessRoles(userDetails, ['DESIGNER', 'PUBLISHER']) && currentDesign === 'document' ? t(
                    `COMPLEX_EMAIL_TOOLBAR:${COMPLEX_EMAIL_TOOLBAR.PREVIEW_AND_SEND_BUTTON.key}`,
                    COMPLEX_EMAIL_TOOLBAR.PREVIEW_AND_SEND_BUTTON.value,
                  ) : t(
                    `COMPLEX_EMAIL_TOOLBAR:${COMPLEX_EMAIL_TOOLBAR.PREVIEW_BUTTON.key}`,
                    COMPLEX_EMAIL_TOOLBAR.PREVIEW_BUTTON.value,
                  )}
            </Button>
            </>
          )}
        </PreviewEndContainer>
        <EndContainer currentBlock={currentBlock} isPreview={isPreview}>
          {!readOnly && <div className="small-switch switch2">
            <Switch
              checked={isTemplateLocked}
              disabled={lockedUserID !== userDetails.id}
              onChange={e => handleReadModeChange(e)}
              loading={isLoading}
              size="small"
            />
            <span className="switch-name">
              {t(
                COMPLEX_DESIGN_TOOLBAR.READ_MODE_LABEL.key,
                COMPLEX_DESIGN_TOOLBAR.READ_MODE_LABEL.value,
              )}
            </span>
          </div>}
        </EndContainer>
      </TemplateName>
      {versionSettingsVisible && artboardVersion === 'draft' && !readOnly ? (
        <ComplexEmailVersion documentId={docInfo.resourceId} />
      ): null}
    </>
  );
};

export default EmailTemplateName;